import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { useStateContext } from '../../../contexts/ContextProvider';
import { usePermissions } from '../../../contexts/PermissionContext';
import { arriveesEnRetard, compteursChambres } from '../../../domain/menage/etatChambres';
import Button from '../../components/Button';
import EtatErreur from '../../components/EtatErreur';
import Text from '../../components/Text';
import { CLE_MENAGE, ECRANS_MOBILES, estEcranEligible } from '../../config/mobileScreens';
import { echecSansDonnee } from '../../helpers/requete';
import { formatDate, langueMobile, t } from '../../i18n/langue';
import { useEtatDesChambres, useObjetsTrouves, usePannesEnCours } from '../../api/useModulesMenage';

import { Carte, Indicateur } from './briques';

/**
 * Les modules ménage du dashboard mobile, pour la réception : état des chambres,
 * pannes en cours, objets trouvés. Lecture seule ; l'accès (permission + module de
 * l'hôtel) est vérifié par widget dans `Dashboard.tsx`, avant tout appel.
 */

const nombre = (n: number | undefined) => new Intl.NumberFormat(langueMobile()).format(n ?? 0);

/**
 * Ouvre un écran du mobile s'il est porté ET accessible à ce compte, sinon `null`.
 * Lu au rendu dans le registre : un écran ajouté plus tard (ex. Maintenance) donne
 * son lien sans toucher ce fichier.
 */
function useOuvrirEcran(trouver: (cle: string, chemin: string) => boolean): (() => void) | null {
    const navigate = useNavigate();
    const { hasPermission, isSuperAdmin } = usePermissions();
    const { currentFacility } = useStateContext();
    const ecran = ECRANS_MOBILES.find((e) => e.porte && trouver(e.cle, e.chemin));
    if (!ecran || !estEcranEligible(ecran, hasPermission, isSuperAdmin === true, currentFacility ?? null)) return null;
    return () => navigate(ecran.chemin);
}

/** Chargement, échec sans donnée, puis le contenu : la même règle que les indicateurs. */
function Corps({ requete, children }: { requete: { isLoading: boolean; isError: boolean; data: unknown; refetch: () => unknown }; children: ReactNode }) {
    if (echecSansDonnee(requete)) return <EtatErreur onReessayer={() => void requete.refetch()} />;
    if (requete.isLoading) {
        return (
            <Text variante="petit" ton="attenue">
                {t('commun.chargement')}
            </Text>
        );
    }
    return <>{children}</>;
}

/** Une ligne de liste, non tapotable : deux lignes de texte. */
function Ligne({ principal, secondaire }: { principal: string; secondaire: string }) {
    return (
        <li className="border-t border-border-color-light py-2 first:border-t-0">
            <Text variante="petit" className="block truncate font-medium">
                {principal}
            </Text>
            <Text variante="petit" ton="attenue" className="block truncate">
                {secondaire}
            </Text>
        </li>
    );
}

function Lien({ libelle, onPress }: { libelle: string; onPress: (() => void) | null }) {
    if (!onPress) return null;
    return <Button libelle={libelle} variante="lien" espacement="aucun" onPress={onPress} />;
}

/** `housekeeping_overview` : où en est le ménage, et les arrivées sont-elles prêtes ? */
export function ModuleEtatChambres() {
    const requete = useEtatDesChambres();
    const ouvrir = useOuvrirEcran((cle) => cle === CLE_MENAGE);
    const d = requete.data;
    const c = compteursChambres(d?.rooms_by_status);
    const total = d?.arrivals_today_total ?? 0;
    const pretes = d?.arrivals_today_ready ?? 0;
    const enRetard = total > 0 && arriveesEnRetard(pretes / total, d?.is_past_arrival_cutoff === true);

    return (
        <Carte
            titre={t('dashboard.chambres.titre')}
            pied={<Lien libelle={t('dashboard.chambres.ouvrirMenage')} onPress={ouvrir} />}
        >
            <Corps requete={requete}>
                <div className="grid grid-cols-2 gap-2">
                    <Indicateur valeur={nombre(c.aFaire)} libelle={t('dashboard.chambres.aFaire')} />
                    <Indicateur valeur={nombre(c.enCours)} libelle={t('dashboard.chambres.enCours')} />
                    <Indicateur valeur={nombre(c.aControler)} libelle={t('dashboard.chambres.aControler')} />
                    <Indicateur valeur={nombre(c.pretes)} libelle={t('dashboard.chambres.pretes')} />
                </div>
                <div className="mt-2.5 flex items-baseline justify-between gap-2">
                    <Text variante="petit" ton="attenue">
                        {t('dashboard.chambres.arriveesPretes')}
                    </Text>
                    {total === 0 ? (
                        <Text variante="petit" ton="attenue">
                            {t('dashboard.chambres.aucuneArrivee')}
                        </Text>
                    ) : (
                        <Text variante="sousTitre" ton={enRetard ? 'danger' : 'marque'} as="span">
                            {pretes}/{total}
                        </Text>
                    )}
                </div>
                {enRetard && d?.arrival_cutoff_time && (
                    <Text variante="petit" ton="danger" className="block">
                        {t('dashboard.chambres.heureCibleDepassee', { heure: d.arrival_cutoff_time })}
                    </Text>
                )}
            </Corps>
        </Carte>
    );
}

/** `maintenance_open` : combien de pannes, et qui s'en occupe. */
export function ModulePannes() {
    const requete = usePannesEnCours();
    const ouvrir = useOuvrirEcran((_, chemin) => chemin === '/maintenance');
    const d = requete.data;
    const pannes = d?.open_issues ?? [];

    return (
        <Carte titre={t('dashboard.pannes.titre')} pied={<Lien libelle={t('dashboard.pannes.voir')} onPress={ouvrir} />}>
            <Corps requete={requete}>
                <Text variante="petit" ton="attenue" className="mb-1.5 block">
                    {t('dashboard.pannes.compteurs', { ouvertes: nombre(d?.total_open), critiques: nombre(d?.total_critical) })}
                </Text>
                {pannes.length === 0 ? (
                    <Text variante="petit" ton="attenue">
                        {t('dashboard.pannes.aucune')}
                    </Text>
                ) : (
                    <ul>
                        {pannes.map((p) => (
                            <Ligne
                                key={p.id}
                                principal={[p.room_name ? t('dashboard.pannes.chambre', { numero: p.room_name }) : p.location_text, p.title].filter(Boolean).join(' · ')}
                                secondaire={p.assigned_to_name ? t('dashboard.pannes.prisPar', { nom: p.assigned_to_name }) : t('dashboard.pannes.personne')}
                            />
                        ))}
                    </ul>
                )}
            </Corps>
        </Carte>
    );
}

const dateCourte = (iso: string) => formatDate(iso, { day: 'numeric', month: 'short' });

/** `lost_found_unclaimed` : ce qui attend son propriétaire. */
export function ModuleObjetsTrouves() {
    const requete = useObjetsTrouves();
    const d = requete.data;
    const objets = d?.recent_unclaimed ?? [];

    return (
        <Carte titre={t('dashboard.objets.titre')}>
            <Corps requete={requete}>
                <Text variante="petit" ton="attenue" className="mb-1.5 block">
                    {t('dashboard.objets.nonReclames', { nombre: nombre(d?.total_unclaimed) })}
                </Text>
                {objets.length === 0 ? (
                    <Text variante="petit" ton="attenue">
                        {t('dashboard.objets.aucun')}
                    </Text>
                ) : (
                    <ul>
                        {objets.map((o) => (
                            <Ligne
                                key={o.id}
                                principal={o.name}
                                secondaire={[
                                    o.room_name ? t('dashboard.objets.chambre', { numero: o.room_name }) : null,
                                    t('dashboard.objets.trouveLe', { date: dateCourte(o.found_at) }),
                                ].filter(Boolean).join(' · ')}
                            />
                        ))}
                    </ul>
                )}
            </Corps>
        </Carte>
    );
}
